/**
 * Findings Management Tab (app portal — facility managers)
 *
 * Lets a facility manager review audit findings for their own facilities,
 * respond to individual findings, and bulk-update finding statuses. Scoped to
 * the manager's accessible facilities via `useFacilityContext`.
 *
 * Reuses the shared audit primitives:
 *  - `FindingCard` (response modal via `showResponseButton`, bulk selection via
 *    `selectable`/`selected`/`onSelectionChange`)
 *  - `BulkActionsToolbar` (`useBulkUpdateFindings`)
 */

import { useState } from "react";
import {
  Stack,
  Card,
  Select,
  Alert,
  Loader,
  Text,
  Group,
  Badge,
  Checkbox,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconInfoCircle,
  IconClipboardCheck,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import api from "../../lib/apiClient";
import { useFacilityContext } from "../../hooks/useFacilityContext";
import { useFacilities } from "../../hooks/useFacilities";
import type { AuditFinding } from "../../types/audit";
import { FindingCard } from "../../components/audit/FindingCard";
import { BulkActionsToolbar } from "../../components/audit/BulkActionsToolbar";

export function FindingsManagementTab() {
  const { currentFacilityId, facilityIds } = useFacilityContext();
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>(
    currentFacilityId || facilityIds[0] || "",
  );
  const [selectedFindingIds, setSelectedFindingIds] = useState<Set<string>>(
    new Set(),
  );

  const { data: facilities } = useFacilities();

  const {
    data: findings = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["facility-audit-findings", selectedFacilityId],
    queryFn: async () => {
      if (!selectedFacilityId) return [] as AuditFinding[];
      const { data } = await api.get(
        `/audit/findings?facility_id=${selectedFacilityId}`,
      );
      return data.data as AuditFinding[];
    },
    enabled: !!selectedFacilityId,
  });

  // Facility selector options, scoped to the manager's facilities.
  const facilityOptions =
    facilities
      ?.filter((f) => facilityIds.includes(f.id))
      .map((f) => ({ value: f.id, label: f.name })) || [];

  const clearSelection = () => setSelectedFindingIds(new Set());

  const handleSelectionChange = (findingId: string, selected: boolean) => {
    setSelectedFindingIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(findingId);
      } else {
        next.delete(findingId);
      }
      return next;
    });
  };

  const handleFacilityChange = (value: string | null) => {
    setSelectedFacilityId(value || facilityIds[0] || "");
    clearSelection();
  };

  const allSelected =
    findings.length > 0 && selectedFindingIds.size === findings.length;
  const someSelected =
    selectedFindingIds.size > 0 && selectedFindingIds.size < findings.length;

  const handleSelectAll = () => {
    if (allSelected) {
      clearSelection();
    } else {
      setSelectedFindingIds(new Set(findings.map((f) => f.id)));
    }
  };

  // US-112 Phase 1b: withdrawn findings stay listed but are excluded from the
  // critical/open compliance counts.
  const criticalCount = findings.filter(
    (f) => f.severity === "critical" && f.withdrawn_at == null,
  ).length;
  const openCount = findings.filter(
    (f) => f.status === "open" && f.withdrawn_at == null,
  ).length;

  return (
    <Stack gap="md">
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        Review and respond to audit findings for your facilities. Select
        multiple findings to update their status in bulk.
      </Alert>

      {facilityIds.length > 1 && (
        <Card withBorder>
          <Select
            label="Facility"
            placeholder="Select facility"
            data={facilityOptions}
            value={selectedFacilityId}
            onChange={handleFacilityChange}
            style={{ maxWidth: 300 }}
          />
        </Card>
      )}

      {/* Bulk actions toolbar (renders nothing when no selection). */}
      <BulkActionsToolbar
        selectedIds={Array.from(selectedFindingIds)}
        onClearSelection={clearSelection}
        onSuccess={() => {
          clearSelection();
          refetch();
        }}
      />

      <Card withBorder>
        {isLoading ? (
          <Stack align="center" py="xl">
            <Loader size="md" />
            <Text c="dimmed">Loading findings...</Text>
          </Stack>
        ) : isError ? (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Failed to load findings. Please try refreshing the page.
          </Alert>
        ) : findings.length === 0 ? (
          <Alert
            icon={<IconClipboardCheck size={16} />}
            color="green"
            title="No Findings"
          >
            There are no audit findings for this facility.
          </Alert>
        ) : (
          <Stack gap="md">
            <Group justify="space-between">
              <Checkbox
                label="Select all"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
              />
              <Group gap="xs">
                <Badge color="red" variant="light">
                  Critical: {criticalCount}
                </Badge>
                <Badge color="blue" variant="light">
                  Open: {openCount}
                </Badge>
                <Badge color="gray" variant="light">
                  Total: {findings.length}
                </Badge>
              </Group>
            </Group>

            <Stack gap="md">
              {findings.map((finding) => (
                <FindingCard
                  key={finding.id}
                  finding={finding}
                  showResponseButton
                  selectable
                  selected={selectedFindingIds.has(finding.id)}
                  onSelectionChange={handleSelectionChange}
                  onUpdate={() => refetch()}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Card>
    </Stack>
  );
}
